import React, { useState, useEffect } from "react";
import {
  Spin,
  Row,
  Col,
  Button,
  Dropdown,
  Menu,
  Modal,
  Table,
  Tag,
} from "antd";
import {
  Users,
  BarChart2,
  UserPlus,
  MoreHorizontal,
  Trash2,
} from "lucide-react";
import { useSelector } from "react-redux";
import moment from "moment";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  TeamInviteModal,
  SuccessNotification,
  ErrorNotification,
  InfoNotification,
} from "@components";
import { getTeamMembers, removeTeamMember } from "@api/user/admin";
import "@assets/css/History.css";

const TeamList = () => {
  const history = useHistory();
  const { user, billingAdmin } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  // Invite Modal
  const [open, setOpen] = useState(false);
  const [team, setTeam] = useState([]);

  const { t } = useTranslation();

  // Handle modal open/close
  const handleInviteMember = () => {
    // If not billingAdmin, don't allow to invite
    if (!billingAdmin) {
      InfoNotification(
        "You are not the billing admin, only the billing admin can invite team members"
      );
      return;
    }

    if (user?.currentPlan === "Free") {
      InfoNotification("Upgrade your plan to invite your team");
      return;
    } else if (user?.currentPlan === "Optimizer" && team?.length > 2) {
      InfoNotification(
        "You have reached the maximum number of team members under your current plan, upgrade to invite more members"
      );
      return;
    } else if (user?.currentPlan === "Wizard" && team?.length > 6) {
      InfoNotification(
        "You have reached the maximum number of team members under your current plan, upgrade to invite more members"
      );
      return;
    } else if (user?.currentPlan === "Maverick" && team?.length > 11) {
      InfoNotification(
        "You have reached the maximum number of team members under your current plan, upgrade to invite more members"
      );
      return;
    } else {
      setOpen(!open);
    }
  };

  // Handle delete account
  const handleRemoveMember = (member) => {
    // If not billingAdmin, don't allow to remove
    if (!billingAdmin) {
      InfoNotification(
        "You are not the billing admin, only the billing admin can remove team members"
      );
      return;
    }

    // Confirm delete
    Modal.confirm({
      title: `Are you sure you want to remove ${member?.name} from the team?`,
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          // Delete event
          removeTeamMember(member.user)
            .then(() => {
              SuccessNotification("User removed from your team successfully");
              // Fetch all team members
              fetchTeamMembers();
            })
            .catch((err) => {
              console.log("err", err);
              ErrorNotification(err.response.data?.type[0].message);
            });
        } catch (error) {
          console.log("------ error", error.response.data?.type[0].message);
          // Show error notification
          ErrorNotification(error.response.data?.type[0].message);
        }
      },
    });
  };

  // Fetch all team members
  const fetchTeamMembers = async () => {
    setLoading(true);
    // Admin
    const { data } = await getTeamMembers();

    // Set team members
    setTeam(data);

    setLoading(false);
  };

  const columns = [
    {
      title: t("teamList.name"),
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              background: "linear-gradient(135deg, #8B5CF6 0%, #a78bfa 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontWeight: "500",
              fontSize: "13px",
            }}
          >
            {text.charAt(0).toUpperCase()}
          </div>
          <span style={{ fontWeight: 500, color: "#1e293b" }}>{text}</span>
        </div>
      ),
    },
    {
      title: t("teamList.email"),
      dataIndex: "email",
      key: "email",
      render: (text) => <span style={{ color: "#64748b" }}>{text}</span>,
    },
    {
      title: t("teamList.joinDate"),
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) => (
        <span style={{ color: "#64748b" }}>
          {moment(text).format("Do, MMMM, YYYY")}
        </span>
      ),
    },
    {
      title: t("teamList.action"),
      dataIndex: "action",
      key: "action",
      render: (text, record) =>
        user?.email === record?.email ? (
          <Tag
            style={{
              background: "linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(167, 139, 250, 0.1) 100%)",
              color: "#8B5CF6",
              border: "1px solid #e9d5ff",
              padding: "4px 12px",
              borderRadius: "6px",
              fontWeight: 500,
            }}
          >
            {t("teamList.me")}
          </Tag>
        ) : (
          <Dropdown
            trigger={["click"]}
            placement="bottomLeft"
            overlay={
              <Menu
                style={{
                  padding: "8px",
                  borderRadius: "8px",
                  border: "1px solid #e9d5ff",
                  boxShadow: "0 4px 12px rgba(139, 92, 246, 0.1)",
                }}
              >
                {user?.role === "admin" && (
                  <Menu.Item
                    key={1}
                    onClick={() => handleRemoveMember(record)}
                    icon={<Trash2 size={16} style={{ color: "#dc2626" }} />}
                    style={{
                      color: "#dc2626",
                      borderRadius: "6px",
                    }}
                  >
                    {t("teamList.remove")}
                  </Menu.Item>
                )}
              </Menu>
            }
          >
            <Button
              size="small"
              style={{
                border: "1px solid #e9d5ff",
                background: "#faf5ff",
                padding: "4px 8px",
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <MoreHorizontal size={18} style={{ color: "#8B5CF6" }} />
            </Button>
          </Dropdown>
        ),
    },
  ];

  // handleViewTeamAnalytics
  const handleViewTeamAnalytics = () => {
    history.push(`/a/ws/${user.workspaceId}/team/analytics`);
  };

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  return (
    <div className="history-container">
      {open && (
        <TeamInviteModal open={open} handleModalClick={handleInviteMember} />
      )}

      <div className="history-content">
        {/* Header */}
        <div className="history-header">
          <div className="history-header-left">
            <div className="history-icon">
              <Users size={20} color="#fff" />
            </div>
            <div>
              <h1 className="history-title">{t("teamList.myTeam")}</h1>
              <p className="history-subtitle">
                Manage your team members and permissions
              </p>
            </div>
          </div>

          <div style={{ display: "flex", gap: "10px" }}>
            {team?.length > 0 && (
              <Button
                onClick={handleViewTeamAnalytics}
                className="history-tab-btn"
                icon={<BarChart2 size={16} />}
              >
                {t("teamList.teamAnalytics")}
              </Button>
            )}

            <Button
              type="primary"
              onClick={handleInviteMember}
              className="history-tab-btn active"
              icon={<UserPlus size={16} />}
            >
              {t("teamList.inviteTeammate")}
            </Button>
          </div>
        </div>

        {/* Table Section */}
        {loading ? (
          <div className="history-loading">
            <Spin size="large" />
            <span>Loading team members...</span>
          </div>
        ) : (
          <div className="history-table-container">
            <Table
              columns={columns}
              dataSource={team}
              rowKey="_id"
              pagination={false}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamList;
